import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { EmployeesService } from '../../employees.service';
import { AuthService } from '../../../authentication/auth.service';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { Employee, EmployeeDetails, EmployeeLocation } from '../../employees.model';
import { getDefaultProfilePicUrl, handleImageError, isInSalesTeamEmployee } from '../../../utils';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Dealer, DealerWithSelection } from '../../../dealers/dealers.model';
import { DealersService } from '../../../dealers/dealers.service';
import { DeleteConfirmationModalComponent } from '../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { WebSocketSubject } from 'rxjs/internal/observable/dom/WebSocketSubject';
import { webSocket } from 'rxjs/webSocket';
import { environment } from '../../../../environments/environment';
import { DesignationEnum } from '../../../common/models/common.model';
import { SALES_TEAM_DESIGNATIONS } from '../../../common/helpers/constants';

@Component({
  selector: 'app-employee-details',
  standalone: false,
  templateUrl: './employee-details.component.html',
  styleUrl: './employee-details.component.scss',
})
export class EmployeeDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('assignDealerContent') assignDealerContent!: TemplateRef<any>;
  @ViewChild('resetPasswordContent') resetPasswordContent!: TemplateRef<any>;

  protected isEmployeeDetailsLoading: boolean = true;
  protected employee: EmployeeDetails | null = null;
  protected empDealers: Dealer[] = [];
  protected modalRef: NgbModalRef | null = null;
  protected dealers: DealerWithSelection[] = [];
  protected isDealerDataLoading: boolean = false;
  protected isMappingDealers: boolean = false;
  protected isExportingProfile: boolean = false;
  protected isExportingMonthlyAttendance: boolean = false;
  protected monthlyAttendanceYear: number = new Date().getFullYear();
  protected monthlyAttendanceMonth: number = new Date().getMonth() + 1;
  protected isExportingYearlyAttendance: boolean = false;
  protected exportAttendanceYear: number = new Date().getFullYear();
  protected isExportingRouteReport: boolean = false;
  protected routeReportYear: number = new Date().getFullYear();
  protected routeReportMonth: number | null = new Date().getMonth() + 1;
  protected isExportingTaReport: boolean = false;
  protected taReportYear: number = new Date().getFullYear();
  protected taReportMonth: number | null = new Date().getMonth() + 1;
  protected isResettingPassword: boolean = false;
  protected resetPasswordError: string | null = null;
  protected newPassword: string = '';
  protected showNewPassword: boolean = false;
  protected wsError: string | null = null;
  protected empLocation: EmployeeLocation | null = null;

  // Google Maps config
  zoom = 15;
  center: google.maps.LatLngLiteral = { lat: 20.5937, lng: 78.9629 };
  markerPositions: google.maps.LatLngLiteral[] = [];

  private employeeId: string | null = null;
  private _subs = new Subscription();
  private socket$!: WebSocketSubject<any>;
  protected lastUpdatedLocationTime: string | null = null;

  protected readonly getDefaultProfilePicUrl = getDefaultProfilePicUrl;
  protected readonly handleImageError = handleImageError;

  protected readonly monthOptions = [
    { label: 'January', value: 1 },
    { label: 'February', value: 2 },
    { label: 'March', value: 3 },
    { label: 'April', value: 4 },
    { label: 'May', value: 5 },
    { label: 'June', value: 6 },
    { label: 'July', value: 7 },
    { label: 'August', value: 8 },
    { label: 'September', value: 9 },
    { label: 'October', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
  ];

  constructor(
    private _employeeService: EmployeesService,
    private _authService: AuthService,
    private _route: ActivatedRoute,
    private _modalService: NgbModal,
    private _dealersService: DealersService,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this._route.paramMap.subscribe(params => {
      this.employeeId = params.get('id');

      this._fetchEmployeeDetails();
      const wsUrl = `${environment.wsUrl}employees/${this.employeeId}/`;
      this.socket$ = webSocket(wsUrl);
      // 🔹 Subscribe to websocket messages
      this._subs.add(
        this.socket$.subscribe({
          next: message => {
            if (message && message.location.latitude && message.location.longitude) {
              this.updateEmployeeLocation(message.location as EmployeeLocation);
              this.lastUpdatedLocationTime = message.location.timestamp;
              this.wsError = null; // clear any old errors
            } else {
              this.wsError = 'No live location data available.';
            }
          },
          error: err => {
            console.error('❌ WS Error', err);
            this.wsError = 'Failed to fetch live location. Please try again later.';
          },
          complete: () => console.log('✅ WS Closed'),
        })
      );
    });
  }

  // Protected Methods

  protected openAssignDealerModal(): void {
    const modalOptions: NgbModalOptions = {
      backdrop: 'static',
      size: 'md',
    };

    this._openModal(this.assignDealerContent, modalOptions);
  }

  protected assignSelectedDealers(): void {
    const employeeId = this.employee?.id;

    // Check if employeeId is missing — prevent continuing if it's not available
    if (!employeeId) {
      return;
    }

    const selectedDealerIds = this.dealers.filter(dealer => dealer.selected).map(dealer => dealer.id);

    if (selectedDealerIds.length === 0) {
      return;
    }

    const reqBody = {
      dealers: selectedDealerIds,
    };

    this._updateDealersMappingWithEmployee(employeeId, reqBody);
  }

  openResetPasswordModal(): void {
    this.newPassword = '';
    this.showNewPassword = false;
    this.resetPasswordError = null;
    this._openModal(this.resetPasswordContent, { centered: true });
  }

  resetPassword(): void {
    if (!this.newPassword || !this.employee?.user?.id) return;

    this.isResettingPassword = true;
    this.resetPasswordError = null;

    this._subs.add(
      this._authService
        .resetPasswordForAdmin(this.employee.user.id.toString(), { password: this.newPassword })
        .pipe(finalize(() => (this.isResettingPassword = false)))
        .subscribe({
          next: () => {
            this.modalRef?.close();
            // Optionally show a success snackbar here if a SnackbarService is available
          },
          error: err => {
            this.resetPasswordError =
              err?.error?.detail || err?.error?.message || 'Failed to reset password. Please try again.';
          },
        })
    );
  }

  protected onUnassignDealer(dealerId: string): void {
    const employeeId = this.employee?.id;
    if (!employeeId) return;

    const modalRef = this._modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    // Set modal content
    modalRef.componentInstance.title = 'Unassign Dealer';
    modalRef.componentInstance.message = 'Are you sure you want to unassign this dealer from the employee?';
    modalRef.componentInstance.warning =
      'This action will remove the dealer from the assigned list but will not delete the dealer record.';

    modalRef.result
      .then(() => {
        // Prepare updated dealer list without the unassigned one
        const updatedDealerIds = this.empDealers.filter(dealer => dealer.id !== dealerId).map(dealer => dealer.id);

        const reqBody = { dealers: updatedDealerIds };

        // Update mapping
        this._updateDealersMappingWithEmployee(employeeId, reqBody);

        // Update selection state in the dealer list
        this.dealers = this.dealers.map(dealer => ({
          ...dealer,
          selected: updatedDealerIds.includes(dealer.id),
        }));
      })
      .catch(() => {
        // Optional: handle cancel/dismiss without any action
      });
  }

  // Private methods

  private _fetchEmployeeDetails(): void {
    if (!this.employeeId) {
      return;
    }

    this.isDealerDataLoading = true;
    this.isEmployeeDetailsLoading = true;

    const employeeDetails$ = this._employeeService.get(this.employeeId);
    const dealerList$ = this._dealersService.list();

    this._subs.add(
      forkJoin([employeeDetails$, dealerList$])
        .pipe(
          finalize(() => {
            this.isEmployeeDetailsLoading = false;
            this.isDealerDataLoading = false;
          })
        )
        .subscribe({
          next: ([employeeDetails, dealersList]) => {
            this.employee = employeeDetails;
            this.empDealers = this.employee?.dealers ?? [];

            this.dealers = dealersList['results'].map((d: any) => ({
              ...d,
              selected: this.empDealers.some(emp => emp.id === d.id),
            }));
          },
        })
    );
  }

  private _openModal(content: TemplateRef<any>, options: NgbModalOptions): void {
    this.modalRef = this._modalService.open(content, options);
  }

  private _updateDealersMappingWithEmployee(employeeId: string, reqBody: any): void {
    this.isMappingDealers = true;

    this._subs.add(
      this._employeeService
        .update(employeeId, reqBody)
        .pipe(finalize(() => (this.isMappingDealers = false)))
        .subscribe({
          next: () => {
            // Sync empDealers with the current dealer list, excluding the unassigned one
            this.empDealers = this.dealers
              .filter(dealer => reqBody.dealers.includes(dealer.id))
              .map(({ selected, ...rest }) => rest); // remove `selected` if needed

            this.modalRef?.dismiss();
          },
          error: err => {
            console.error('Failed to mapping dealer:', err);
          },
        })
    );
  }

  private updateEmployeeLocation(location: EmployeeLocation): void {
    this.empLocation = location;
    this.center = { lat: location.latitude, lng: location.longitude };
    this.markerPositions = [{ lat: location.latitude, lng: location.longitude }];
  }

  protected onExportMonthlyAttendance(): void {
    if (!this.employeeId) return;

    this.isExportingMonthlyAttendance = true;

    const fullName = `${this.employee?.user?.first_name ?? ''}_${this.employee?.user?.last_name ?? ''}`.trim();
    const filename = `attendance_${fullName}_${this.monthlyAttendanceYear}_${this.monthlyAttendanceMonth}.xlsx`;

    this._subs.add(
      this._employeeService
        .exportMonthlyAttendanceById(this.employeeId, this.monthlyAttendanceYear, this.monthlyAttendanceMonth)
        .pipe(finalize(() => (this.isExportingMonthlyAttendance = false)))
        .subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
          },
        })
    );
  }

  protected onExportYearlyAttendance(): void {
    if (!this.employeeId) return;

    this.isExportingYearlyAttendance = true;

    this._subs.add(
      this._employeeService
        .exportYearlyAttendance(this.employeeId, this.exportAttendanceYear)
        .pipe(finalize(() => (this.isExportingYearlyAttendance = false)))
        .subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `employee_${this.employeeId}_attendance_${this.exportAttendanceYear}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
          },
        })
    );
  }

  protected onExportRouteReport(): void {
    if (!this.employeeId) return;

    this.isExportingRouteReport = true;

    const month = this.routeReportMonth;
    const filename =
      month !== null
        ? `route_report_${this.employeeId}_${String(month).padStart(2, '0')}_${this.routeReportYear}.xlsx`
        : `route_report_${this.employeeId}_${this.routeReportYear}.xlsx`;

    this._subs.add(
      this._employeeService
        .exportRouteReport(this.employeeId, this.routeReportYear, month)
        .pipe(finalize(() => (this.isExportingRouteReport = false)))
        .subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
          },
        })
    );
  }

  protected onExportTaReport(): void {
    if (!this.employeeId) return;

    this.isExportingTaReport = true;

    const month = this.taReportMonth;
    const filename =
      month !== null
        ? `ta_report_${this.employeeId}_${String(month).padStart(2, '0')}_${this.taReportYear}.xlsx`
        : `ta_report_${this.employeeId}_${this.taReportYear}.xlsx`;

    this._subs.add(
      this._employeeService
        .exportTaReport(this.employeeId, this.taReportYear, month)
        .pipe(finalize(() => (this.isExportingTaReport = false)))
        .subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
          },
        })
    );
  }

  protected onExportProfile(): void {
    if (!this.employeeId) return;

    this.isExportingProfile = true;

    this._subs.add(
      this._employeeService
        .exportProfile(this.employeeId)
        .pipe(finalize(() => (this.isExportingProfile = false)))
        .subscribe({
          next: (blob: Blob) => {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `employee_${this.employeeId}_profile.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
          },
        })
    );
  }

  protected onBackToEmployees(): void {
    void this._router.navigate(['/employees']);
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
    this.modalRef?.dismiss();
  }

  protected readonly DesignationEnum = DesignationEnum;
  protected readonly SALES_TEAM_DESIGNATIONS = SALES_TEAM_DESIGNATIONS;
  protected readonly isInSalesTeamEmployee = isInSalesTeamEmployee;
}
